import React, { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { Alert, Button, Empty, Spin } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import StoreReviewDrawer from "../StoreApproval/components/StoreReviewDrawer";
import type { StoreApprovalItem } from "../StoreApproval/types";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import martEmptyImg from "assets/images/mart-empty.png";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { clearMarketError, fetchMarketById } from "store/slices/marketSlice";
import {
  clearAdminStoreError,
  clearMarketStores,
  fetchStoresByMarket,
} from "store/slices/adminStoreSlice";
import { mapAdminStoreToApprovalItem } from "../StoreApproval/storeMappers";

interface MarketStoresPageProps {}

export const MarketStoresPage: React.FunctionComponent<
  MarketStoresPageProps
> = () => {
  const { marketId } = useParams();
  const dispatch = useAppDispatch();
  const { detailLoading, error: marketError, selectedMarket } = useAppSelector(
    (state) => state.markets
  );
  const {
    error: storesError,
    marketStores,
    marketStoresLoading,
  } = useAppSelector((state) => state.adminStores);
  const [reviewItem, setReviewItem] = useState<StoreApprovalItem | null>(null);
  const numericMarketId = Number(marketId);
  const hasMarketId = Boolean(marketId) && Number.isFinite(numericMarketId);
  const routeError = hasMarketId ? null : "Market id is missing.";

  useEffect(() => {
    if (!hasMarketId) {
      dispatch(clearMarketStores());
      return;
    }

    dispatch(fetchStoresByMarket(numericMarketId));
    dispatch(fetchMarketById(numericMarketId));
  }, [dispatch, hasMarketId, numericMarketId]);

  const stores = useMemo(
    () =>
      marketStores.map((store) =>
        mapAdminStoreToApprovalItem(
          store,
          selectedMarket?.name,
          selectedMarket?.location
        )
      ),
    [marketStores, selectedMarket?.location, selectedMarket?.name]
  );

  const isLoading = marketStoresLoading || detailLoading;
  const title = selectedMarket?.name || "Market Stores";

  return (
    <React.Fragment>
      <CmsTopBar title={title} />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          <div className="markets-section-header mb-3">
            <div className="market-stores-heading-wrap">
              <Link to="/markets" className="market-stores-back">
                <ArrowLeftOutlined />
              </Link>
              <h2 className="markets-heading mb-0">Stores ({stores.length})</h2>
            </div>
          </div>

          {(routeError || storesError || marketError) && (
            <Alert
              type="error"
              message={routeError || storesError || marketError}
              showIcon
              closable
              className="mb-3"
              onClose={() => {
                dispatch(clearAdminStoreError());
                dispatch(clearMarketError());
              }}
            />
          )}

          {isLoading ? (
            <div className="markets-empty-wrap">
              <Spin size="large" />
            </div>
          ) : stores.length === 0 ? (
            <div className="markets-empty-wrap">
              <Empty
                image={
                  <div
                    style={{
                      height: 130,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={martEmptyImg}
                      alt="No stores"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </div>
                }
                description={
                  <div style={{ textAlign: "center", marginTop: 12 }}>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: "#1f1f1f",
                      }}
                    >
                      No stores found for this market.
                    </div>
                  </div>
                }
              />
            </div>
          ) : (
            <div className="market-stores-grid">
              {stores.map((store) => {
                const isInactive =
                  store.activeStatus?.toLowerCase().includes("inactive") ||
                  store.activeStatus === "false";

                return (
                  <div key={store.id} className="market-stores-card">
                    <div className="market-stores-card-left">
                      <div className="market-stores-card-icon market-stores-card-image">
                        <img
                          src={store.logoUrl || martEmptyImg}
                          alt={store.storeName}
                          onError={(event) => {
                            if (!event.currentTarget.src.endsWith(martEmptyImg)) {
                              event.currentTarget.src = martEmptyImg;
                            }
                          }}
                        />
                      </div>
                      <div className="market-stores-card-info">
                        <div className="market-stores-card-name">
                          {store.storeName}
                        </div>
                        <div className="market-stores-card-owner">
                          Owner: {store.ownerName}
                        </div>
                        {store.location && (
                          <div className="market-stores-card-address">
                            {store.location}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="market-stores-card-right">
                      <Button
                        size="small"
                        className="market-stores-btn market-stores-btn-review"
                        onClick={() => setReviewItem(store)}
                      >
                        Review
                      </Button>
                      <div
                        className={
                          isInactive
                            ? "market-stores-status market-stores-status--inactive"
                            : "market-stores-status"
                        }
                      >
                        {isInactive ? "Inactive" : "Active"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Container>
      </Container>

      <StoreReviewDrawer
        open={!!reviewItem}
        item={reviewItem}
        onClose={() => setReviewItem(null)}
        onApprove={() => {}}
        onDecline={() => {}}
        hideActions
      />
    </React.Fragment>
  );
};

export default MarketStoresPage;
